import { useCallback, useEffect, useState } from "react";
import { ProfitabilityCalculator } from "../components/ProfitabilityCalculator";
import { useDateRange } from "../context/DateRangeContext";
import { useFilters } from "../context/FilterContext";

interface ActualsData {
  cashCollected: number;
  closerPayouts: number;
  setterPayouts: number;
  takeHome: number;
  totalExpenses: number;
  netProfit: number;
  callsBooked?: number;
  showRate?: number;
  closeRate?: number;
  avgDealSize?: number;
}

interface DashboardSummaryMetrics {
  totalCashCollected: number;
  closeRate: number;
  showRate: number;
  callsDue: number;
  callsTaken: number;
  avgCashPerClose: number;
}

interface DashboardSummaryResponse {
  success: boolean;
  data: {
    metrics: DashboardSummaryMetrics;
  };
}

export function RevProjector() {
  const { formatForApi } = useDateRange();
  const { funnels, closers, setters } = useFilters();
  const [actuals, setActuals] = useState<ActualsData | null>(null);
  const [isLoading, setIsLoading] = useState(true);

  const fetchSummary = useCallback(async () => {
    try {
      setIsLoading(true);
      const dates = formatForApi();
      const params = new URLSearchParams({
        startDate: dates.startDate,
        endDate: dates.endDate,
      });

      if (funnels.length > 0) params.set("funnel", funnels.join(","));
      if (closers.length > 0) params.set("closer", closers.join(","));
      if (setters.length > 0) params.set("setter", setters.join(","));

      const res = await fetch(`/api/dashboard/summary?${params.toString()}`);
      const data = (await res.json()) as DashboardSummaryResponse;

      if (data.success) {
        const m = data.data.metrics;
        setActuals({
          cashCollected: m.totalCashCollected || 0,
          // These aren't tracked in Relentless Coaching's summary — default to 0
          closerPayouts: 0,
          setterPayouts: 0,
          takeHome: 0,
          totalExpenses: 0,
          netProfit: 0,
          callsBooked: m.callsDue || 0,
          showRate: m.showRate > 0 ? m.showRate / 100 : undefined,
          closeRate: m.closeRate > 0 ? m.closeRate / 100 : undefined,
          avgDealSize: m.avgCashPerClose || undefined,
        });
      }
    } finally {
      setIsLoading(false);
    }
  }, [formatForApi, funnels, closers, setters]);

  useEffect(() => {
    fetchSummary();
  }, [fetchSummary]);

  return (
    <div className="mx-auto flex max-w-[1600px] flex-col gap-6">
      <div className="space-y-2">
        <h1 className="text-3xl font-semibold tracking-tight">Rev Projector</h1>
        <p className="text-sm text-muted-foreground">
          Scenario modeling based on the current date range and filters.
        </p>
      </div>

      {isLoading && !actuals ? (
        <div className="rounded-2xl border border-border bg-card p-6">
          <div className="h-[720px] animate-pulse rounded-xl bg-muted/40" />
        </div>
      ) : (
        <ProfitabilityCalculator actuals={actuals} />
      )}
    </div>
  );
}
